import { getApiEndpoint } from '@/config/api';
import { apiJson, authHeaders } from '@/lib/api-client';

export type SubscriptionPlan = 'freemium' | 'porto2life' | 'live';

export interface User {
  id: number;
  email: string;
  name: string;
  phone?: string;
  user_type: 'guest' | 'owner' | 'admin';
  plan_id?: number;
  subscription_plan?: SubscriptionPlan;
  cpf?: string;
  address?: string;
  birth_date?: string;
  password_hash: string;
  created_at: string;
  updated_at: string;
  is_active: boolean;
  approval_status: 'pending' | 'approved' | 'rejected';
}

export interface AuthResponse {
  user: User;
  token: string;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface CreateUser {
  email: string;
  password_hash: string;
  name: string;
  phone?: string;
  phone_ddi?: string;
  user_type: 'guest' | 'owner' | 'admin';
  privacy_consent?: boolean;
  partner_referral_token?: string | null;
}

class AuthService {
  // Gerar token JWT simples
  private generateToken(user: User): string {
    const payload = {
      id: user.id,
      email: user.email,
      user_type: user.user_type,
      name: user.name,
      timestamp: Date.now()
    };
    
    // Token simples baseado em base64 (n?o ? seguro, apenas para demo)
    return btoa(JSON.stringify(payload));
  }

  // Verificar token simples
  private verifyToken(token: string): any {
    try {
      const decoded = JSON.parse(atob(token));
      // Verificar se o token n?o expirou (24 horas)
      const isExpired = Date.now() - decoded.timestamp > 24 * 60 * 60 * 1000;
      return isExpired ? null : decoded;
    } catch (error) {
      console.error('Erro ao verificar token:', error);
      return null;
    }
  }

  // Hash de senha usando bcrypt (removido - agora feito no backend)
  // private async hashPassword(password: string): Promise<string> {
  //   const saltRounds = 10;
  //   return await bcrypt.hash(password, saltRounds);
  // }


  // Login
  async login(credentials: LoginCredentials): Promise<AuthResponse | null> {
    try {
      console.log('?? AuthService.login chamado:', { email: credentials.email });
      
      // Chamar endpoint de login no backend
      const response = await fetch(getApiEndpoint('/api/auth/login'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: credentials.email,
          password: credentials.password,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
        console.error('? Erro no login:', errorData);
        return null;
      }

      const data = await response.json();
      
      if (!data.user || !data.token) {
        console.error('? Resposta inv?lida do servidor');
        return null;
      }

      const user = AuthService.normalizeUserPlan(data.user as User) as User;
      const token = data.token;

      console.log('? Login bem-sucedido:', { id: user.id, email: user.email, user_type: user.user_type });
      
      // Salvar no localStorage
      localStorage.setItem('authToken', token);
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('isAuthenticated', 'true');
      localStorage.setItem('userType', user.user_type);
      localStorage.setItem('userId', user.id.toString());

      return {
        user,
        token
      };
    } catch (error) {
      console.error('Erro no login:', error);
      return null;
    }
  }

  // Registro
  async register(userData: CreateUser): Promise<AuthResponse | null> {
    try {
      console.log('?? AuthService.register chamado:', { email: userData.email, user_type: userData.user_type });
      
      // Chamar endpoint de registro no backend
      const response = await fetch(getApiEndpoint('/api/auth/register'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: userData.email,
          password: userData.password_hash, // Enviar senha em texto plano, o backend far? o hash
          name: userData.name,
          phone: userData.phone,
          phone_ddi: userData.phone_ddi,
          user_type: userData.user_type,
          privacy_consent: userData.privacy_consent,
          partner_referral_token: userData.partner_referral_token
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
        console.error('? Erro no registro:', errorData);
        throw new Error(errorData.error || 'Erro ao registrar usu?rio');
      }

      const data = await response.json();
      
      if (!data.user) {
        console.error('? Resposta inv?lida do servidor');
        throw new Error('Erro ao registrar usu?rio');
      }

      const user = AuthService.normalizeUserPlan(data.user as User) as User;
      const token = data.token;

      console.log('? Registro bem-sucedido:', { id: user.id, email: user.email, user_type: user.user_type });
      
      // Se for propriet?rio pendente, n?o salvar token
      if (user.user_type === 'owner' && user.approval_status === 'pending') {
        return {
          user,
          token: null
        };
      }

      // Para outros usu?rios, salvar no localStorage
      if (token) {
        localStorage.setItem('authToken', token);
        localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('isAuthenticated', 'true');
        localStorage.setItem('userType', user.user_type);
        localStorage.setItem('userId', user.id.toString());
      }

      return {
        user,
        token: token || null
      };
    } catch (error) {
      console.error('Erro no registro:', error);
      throw error;
    }
  }

  // Logout
  logout(): void {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('userType');
    localStorage.removeItem('userId');
  }

  // Obter dados de autentica??o do localStorage
  getAuthData(): {
    token: string | null;
    user: User | null;
    isAuthenticated: boolean;
    userType: string | null;
    userId: number | null;
  } {
    const token = localStorage.getItem('authToken');
    const userStr = localStorage.getItem('user');
    const rawUser = userStr ? (JSON.parse(userStr) as User) : null;
    const user = rawUser ? AuthService.normalizeUserPlan(rawUser) : null;
    if (user && rawUser && user.subscription_plan !== rawUser.subscription_plan) {
      localStorage.setItem('user', JSON.stringify(user));
    }
    const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
    const userTypeFromStorage = localStorage.getItem('userType');
    const userType = user?.user_type ?? userTypeFromStorage;
    if (user?.user_type && userTypeFromStorage !== user.user_type) {
      localStorage.setItem('userType', user.user_type);
    }
    const userId = localStorage.getItem('userId') ? parseInt(localStorage.getItem('userId')!) : null;

    // Valida??o adicional: verificar se o token ? v?lido
    let validToken = false;
    if (token) {
      try {
        validToken = this.isTokenValid(token);
        if (!validToken) {
          console.log('?? Token inv?lido detectado, limpando sess?o');
          this.logout();
        }
      } catch (error) {
        console.error('? Erro ao validar token:', error);
        this.logout();
      }
    }

    // Se n?o h? token v?lido, limpar tudo
    if (!token || !validToken) {
      return {
        token: null,
        user: null,
        isAuthenticated: false,
        userType: null,
        userId: null
      };
    }

    return {
      token,
      user,
      isAuthenticated: validToken && isAuthenticated,
      userType,
      userId
    };
  }

  // Verificar se token ? v?lido
  isTokenValid(token: string): boolean {
    try {
      const decoded = this.verifyToken(token);
      return decoded !== null;
    } catch (error) {
      return false;
    }
  }

  /** Mapeia plan_id para slug de exibi??o (alinhado ? API / tabela `plans` legada) */
  private static planIdToSlug(planId: number | undefined | null): SubscriptionPlan {
    if (planId === 2) return 'porto2life';
    if (planId === 3) return 'live';
    return 'freemium';
  }

  /**
   * Alinha `subscription_plan` com `plan_id` quando este existe.
   * Evita menu "Meus im?veis" oculto para quem tem plan_id=2/3 mas a coluna
   * `subscription_plan` ainda est? como freemium ou com valor de webhook (ex. active).
   */
  static normalizeUserPlan(user: User | null): User | null {
    if (!user) return null;
    if (user.plan_id != null && user.plan_id > 0) {
      return {
        ...user,
        subscription_plan: AuthService.planIdToSlug(user.plan_id),
      };
    }
    if (user.subscription_plan) {
      return user;
    }
    return { ...user, subscription_plan: 'freemium' };
  }

  async getUserById(id: number): Promise<User | null> {
    try {
      const raw = await apiJson<User>(`api/auth/user/${id}`);
      return raw ? AuthService.normalizeUserPlan(raw as User) : null;
    } catch (error) {
      console.error('Erro ao buscar usu?rio:', error);
      return null;
    }
  }

  // Alterar senha do usu?rio
  async changePassword(userId: number, currentPassword: string, newPassword: string): Promise<boolean> {
    try {
      console.log('?? AuthService.changePassword chamado:', { userId, currentPasswordLength: currentPassword.length, newPasswordLength: newPassword.length });
      
      // Chamar endpoint de altera??o de senha no backend
      const response = await fetch(getApiEndpoint('/api/auth/change-password'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders(),
        },
        body: JSON.stringify({
          userId,
          currentPassword,
          newPassword
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
        console.error('? Erro ao alterar senha:', errorData);
        return false;
      }

      const data = await response.json();
      console.log('? Senha alterada com sucesso');
      return data.success === true;
    } catch (error) {
      console.error('? Erro ao alterar senha:', error);
      return false;
    }
  }

  async updateUser(id: number, userData: Partial<User>): Promise<User | null> {
    try {
      const body: Record<string, unknown> = {};
      if (userData.name) body.name = userData.name;
      if (userData.phone) body.phone = userData.phone;
      if ((userData as User & { phone_ddi?: string }).phone_ddi) {
        body.phone_ddi = (userData as User & { phone_ddi?: string }).phone_ddi;
      }
      if (userData.email) body.email = userData.email;
      if (userData.cpf !== undefined) {
        body.cpf = userData.cpf
          ? String(userData.cpf).replace(/\D/g, '')
          : null;
      }
      if (userData.address !== undefined) body.address = userData.address || null;
      if (userData.birth_date !== undefined) {
        body.birth_date = userData.birth_date || null;
      }
      if (Object.keys(body).length === 0) return null;

      const updatedUser = await apiJson<User>(`api/auth/profile/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
      const normalized = AuthService.normalizeUserPlan(updatedUser);
      if (normalized) {
        localStorage.setItem('user', JSON.stringify(normalized));
        window.dispatchEvent(
          new CustomEvent('userUpdated', { detail: { user: normalized } }),
        );
      }
      return normalized;
    } catch (error) {
      console.error('Erro ao atualizar usu?rio:', error);
      return null;
    }
  }
}

export const authService = new AuthService();